import { Link } from "react-router-dom";
import { ArrowUpRight, Globe, Linkedin, Mail, MapPin } from "lucide-react";
import { Logo } from "../Logo";

const YEAR = new Date().getFullYear();

function Col({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</h4>
      <ul className="mt-4 space-y-2.5 text-sm text-slate-400">{children}</ul>
    </div>
  );
}

function Ext({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 transition hover:text-white"
    >
      {children}
      <ArrowUpRight className="h-3 w-3 opacity-50" />
    </a>
  );
}

export function Footer() {
  return (
    <footer className="bg-slate-950 text-slate-400">
      <div className="mx-auto max-w-6xl px-6 py-16">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-[1.6fr_1fr_1fr_1fr]">
          {/* Brand + contact */}
          <div>
            <Logo light size={34} />
            <p className="mt-4 max-w-sm text-sm leading-relaxed">
              Connect, automate, and query every enterprise system through natural language —
              powered by Wise 2.0.
            </p>
            <div className="mt-6 space-y-2.5 text-sm">
              <a
                href="mailto:info@shineywise.com"
                className="flex items-center gap-2.5 transition hover:text-white"
              >
                <Mail className="h-4 w-4 text-sky-400" /> info@shineywise.com
              </a>
              <a
                href="https://shineywise.com"
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2.5 transition hover:text-white"
              >
                <Globe className="h-4 w-4 text-sky-400" /> shineywise.com
              </a>
              <a
                href="https://www.linkedin.com/company/shineywise"
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2.5 transition hover:text-white"
              >
                <Linkedin className="h-4 w-4 text-sky-400" /> LinkedIn
              </a>
              <p className="flex items-start gap-2.5 leading-relaxed">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-sky-400" />
                <span>
                  19th Floor, Tower C, Bhutani Alphathum,<br />
                  Sector 90, Noida, Uttar Pradesh 201304
                </span>
              </p>
            </div>
          </div>

          <Col title="Product">
            <li><Link to="/app/connections" className="transition hover:text-white">Connections</Link></li>
            <li><Link to="/app/exchange" className="transition hover:text-white">Connector Exchange</Link></li>
            <li><Link to="/app/workflows" className="transition hover:text-white">Workflows</Link></li>
            <li><Link to="/app/copilot" className="transition hover:text-white">Copilot</Link></li>
          </Col>

          <Col title="Company">
            <li><Link to="/about" className="transition hover:text-white">About us</Link></li>
            <li><Link to="/contact" className="transition hover:text-white">Contact us</Link></li>
            <li><Ext href="https://shineywise.com">Find jobs</Ext></li>
            <li><Ext href="https://workspace.shineywise.com/login">Workspace</Ext></li>
          </Col>

          <Col title="Legal">
            <li><Link to="/privacy" className="transition hover:text-white">Privacy Policy</Link></li>
            <li><Link to="/terms" className="transition hover:text-white">Terms of Service</Link></li>
            <li><Link to="/privacy#security" className="transition hover:text-white">Security</Link></li>
          </Col>
        </div>

        <div className="mt-14 border-t border-white/10 pt-6">
          <div className="flex flex-col items-center justify-between gap-3 text-sm sm:flex-row">
            <span>© {YEAR} Shineywise Technologies. All rights reserved.</span>
            <span className="flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />
              All systems operational
            </span>
          </div>
          <p className="mt-3 text-center text-xs text-slate-600 sm:text-left">
            Shineywise Technologies OPC Pvt.&nbsp;Ltd. · Shineywise Technologies LLC
          </p>
        </div>
      </div>
    </footer>
  );
}

/** Compact footer for the authenticated app shell. */
export function AppFooter() {
  return (
    <footer className="flex flex-col items-center justify-between gap-2 border-t border-slate-200 px-6 py-4 text-xs text-slate-400 sm:flex-row">
      <span>© {YEAR} Shineywise Technologies. All rights reserved.</span>
      <div className="flex items-center gap-4">
        <a href="mailto:info@shineywise.com" className="hover:text-slate-600">info@shineywise.com</a>
        <Link to="/privacy" className="hover:text-slate-600">Privacy</Link>
        <Link to="/terms" className="hover:text-slate-600">Terms</Link>
        <span className="text-slate-300">v0.1.0</span>
      </div>
    </footer>
  );
}
